import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import Section from '../components/Section'
import LoadingSpinner from '../components/LoadingSpinner'
import roleApi from '../api/role/RoleApi'
import Form from '../components/forms/Form'
import TextInput from '../components/forms/TextInput'
import LayoutSingleView from '../components/layout/LayoutSingleView'

// Define the form schema using Zod
const roleSchema = z.object({
    jobTitle: z.string().min(1, 'Job title is required'),
    searchTerm: z.string().optional(),
})

// Infer TypeScript types from schema
type RoleFormData = z.infer<typeof roleSchema>

function UpdateRole(): React.JSX.Element {
    const navigate = useNavigate()
    const [apiError, setApiError] = useState<string | undefined>(undefined)
    const [searchParams] = useSearchParams()
    const roleId = searchParams.get('id')
    const [isLoading, setIsLoading] = useState<boolean>(true)

    // Initialize form with React Hook Form
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<RoleFormData>({
        resolver: zodResolver(roleSchema),
    })

    // Handle form submission
    const onSubmit = async (data: RoleFormData) => {
        setApiError(undefined) // Reset API error on new submission

        try {
            await roleApi.updateRole({
                roleId: roleId!,
                jobTitle: data.jobTitle,
                searchTerm: data.searchTerm,
            })
            navigate(`${Pages.ManageRole}?id=${roleId}`)
        } catch (error) {
            console.error(error)

            if (error instanceof Error) {
                setApiError(error.message)
            } else {
                setApiError(
                    'An unknown error occurred. Please try again later.',
                )
            }
        }
    }

    const refreshRole = useCallback(async () => {
        if (!roleId) {
            return
        }

        try {
            const role = (
                await roleApi.getLite({
                    roleId,
                })
            ).role
            reset({
                jobTitle: role.jobTitle,
                searchTerm: role.searchTerm,
            })
        } finally {
            setIsLoading(false)
        }
    }, [roleId, reset])

    useEffect(() => {
        refreshRole()
    }, [roleId, refreshRole])

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section title="New Role">
                <LoadingSpinner
                    show={isSubmitting || isLoading}
                    fullScreen={true}
                />

                <Form
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    apiError={apiError}
                    entityName="role"
                    cancelTo={`${Pages.ManageRole}?id=${roleId}`}
                    isSubmitting={isSubmitting}
                >
                    <TextInput
                        id="jobTitle"
                        name="Job Title"
                        placeholder="Job Title..."
                        register={register}
                        errors={errors}
                    />
                    <TextInput
                        id="searchTerm"
                        name="Search Term"
                        placeholder="Java developer with experience in finance..."
                        register={register}
                        errors={errors}
                    />
                </Form>
            </Section>
        </LayoutSingleView>
    )
}

export default UpdateRole
